import type { AppSettings, CipherPacket, PinEnvelope } from '@safewallet/core';
import { DEFAULT_SETTINGS } from '@safewallet/core';

const DB_NAME = 'safe-wallet-device';
const DB_VERSION = 1;
const STORE = 'state';
const LEGACY_DEVICE_KEY = 'device';
const CURRENT_VAULT_KEY = 'currentVaultId';
const DEVICE_PROFILE_PREFIX = 'device:vault:';
const SETTINGS_KEY = 'settings';
const PAIRING_KEY = 'pairing';

export interface DeviceState {
  version: 1;
  deviceId: string;
  deviceName: string;
  walletLabel?: string;
  vaultId: string;
  /** Absent once fingerprint unlock is on: the secret then lives only in wrappedDeviceSecret. */
  deviceSecret?: string;
  deviceAuthToken: string;
  pinEnvelope: PinEnvelope;
  helloCredentialId?: string;
  helloPrfSalt?: string;
  wrappedDeviceSecret?: CipherPacket;
  accountUserId?: string;
  accountEmail?: string;
  failedUnlocks?: number;
  retryAfter?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PendingPairingState {
  requestId: string;
  deviceId: string;
  deviceName: string;
  displayCode: string;
  publicKey: string;
  privateKey: string;
  vaultId: string;
  expiresAt: string;
}

function deviceProfileKey(vaultId: string): string {
  return `${DEVICE_PROFILE_PREFIX}${vaultId}`;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open device storage.'));
  });
}

async function getValue<T>(key: string): Promise<T | null> {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      request.onsuccess = () => resolve((request.result as T | undefined) ?? null);
      request.onerror = () => reject(request.error ?? new Error('Could not read device storage.'));
    });
  } finally {
    db.close();
  }
}

async function getAllValues<T>(): Promise<T[]> {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      request.onsuccess = () => resolve((request.result as T[] | undefined) ?? []);
      request.onerror = () => reject(request.error ?? new Error('Could not read device storage.'));
    });
  } finally {
    db.close();
  }
}

async function setValue<T>(key: string, value: T): Promise<void> {
  const db = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).put(value, key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Could not save device storage.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Device save was cancelled.'));
    });
  } finally {
    db.close();
  }
}

async function deleteValue(key: string): Promise<void> {
  const db = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).delete(key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Could not clear device storage.'));
    });
  } finally {
    db.close();
  }
}

function isDeviceState(value: unknown): value is DeviceState {
  return Boolean(value && typeof value === 'object'
    && (value as DeviceState).version === 1
    && typeof (value as DeviceState).deviceId === 'string'
    && typeof (value as DeviceState).vaultId === 'string'
    && typeof (value as DeviceState).deviceName === 'string');
}

export async function loadDeviceStates(): Promise<DeviceState[]> {
  const all = await getAllValues<unknown>();
  const profiles = new Map<string, DeviceState>();
  for (const value of all) {
    if (isDeviceState(value)) profiles.set(value.vaultId, value);
  }
  const legacy = await getValue<DeviceState>(LEGACY_DEVICE_KEY);
  if (legacy) profiles.set(legacy.vaultId, legacy);
  return [...profiles.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function loadDeviceState(): Promise<DeviceState | null> {
  const currentVaultId = await getValue<string>(CURRENT_VAULT_KEY);
  if (currentVaultId) {
    const current = await getValue<DeviceState>(deviceProfileKey(currentVaultId));
    if (current) return current;
  }

  const legacy = await getValue<DeviceState>(LEGACY_DEVICE_KEY);
  if (legacy) {
    await saveDeviceState(legacy);
    await deleteValue(LEGACY_DEVICE_KEY);
    return legacy;
  }

  const [first] = await loadDeviceStates();
  if (first) {
    await switchDeviceState(first.vaultId);
    return first;
  }
  return null;
}

export async function saveDeviceState(state: DeviceState): Promise<void> {
  await setValue(deviceProfileKey(state.vaultId), state);
  await setValue(CURRENT_VAULT_KEY, state.vaultId);
}

export async function switchDeviceState(vaultId: string): Promise<DeviceState> {
  const next = await getValue<DeviceState>(deviceProfileKey(vaultId));
  if (!next) throw new Error('That local wallet is no longer available in this browser.');
  await setValue(CURRENT_VAULT_KEY, vaultId);
  return next;
}

export async function deleteDeviceState(vaultId?: string): Promise<void> {
  // Scoped removal: drop only this wallet's profile. Never clear the shared
  // pointer for a different wallet or the legacy key, so other wallets on this
  // browser are untouched.
  if (vaultId) {
    await deleteValue(deviceProfileKey(vaultId));
    const currentVaultId = await getValue<string>(CURRENT_VAULT_KEY);
    if (currentVaultId === vaultId) await deleteValue(CURRENT_VAULT_KEY);
    return;
  }
  const currentVaultId = await getValue<string>(CURRENT_VAULT_KEY);
  if (currentVaultId) await deleteValue(deviceProfileKey(currentVaultId));
  await deleteValue(CURRENT_VAULT_KEY);
  await deleteValue(LEGACY_DEVICE_KEY);
}

export async function loadSettings(): Promise<AppSettings> {
  return (await getValue<AppSettings>(SETTINGS_KEY)) ?? DEFAULT_SETTINGS;
}

export const saveSettings = (settings: AppSettings) => setValue(SETTINGS_KEY, settings);

export const loadPendingPairing = () => getValue<PendingPairingState>(PAIRING_KEY);
export const savePendingPairing = (state: PendingPairingState) => setValue(PAIRING_KEY, state);
export const clearPendingPairing = () => deleteValue(PAIRING_KEY);
